export enum CartType {
  ECOMMERCE = "ECOMMERCE",
  MLM = "MLM",
}

export enum CartStatus {
  ACTIVE = "ACTIVE",
  CHECKOUT = "CHECKOUT",
  COMPLETED = "COMPLETED",
  ABANDONED = "ABANDONED",
}

export enum CouponType {
  ADMIN = "ADMIN",
  INFLUENCER = "INFLUENCER",
  SELLER = "SELLER",
}

export enum ProductType {
  PHYSICAL = "PHYSICAL",
  DIGITAL = "DIGITAL",
  MLM_VOUCHER = "MLM_VOUCHER",
}

export interface CartItem {
  id: string;
  cartId: string;
  productId: string;
  productName: string;
  productImage: string | null;
  productType: ProductType;
  variantId: string | null;
  variantName: string | null;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  discountAmount: number;
  finalPrice: number;
  sellerId: string | null;
  sellerName: string | null;
  referralId: string | null;
  isMlmProduct: boolean;
  savedForLater?: boolean;
  savedAt?: string | null;
  metadata: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
}

export interface CartSummary {
  itemsTotal: number;
  itemsCount: number;
  couponDiscount: number;
  salarCodeDiscount: number;
  voucherDiscount: number;
  totalDiscount: number;
  subtotal: number;
  deliveryCharges: number;
  platformFee: number;
  taxAmount: number;
  gstAmount: number;
  grandTotal: number;
  savedAmount: number;
  salarCoinsEarned: number;
  savedItemsCount?: number;
}

export interface Cart {
  id: string;
  userId: string;
  cartType: CartType;
  status: CartStatus;
  referralId: string | null;
  invitationCode: string | null;
  selectedAddressId: string | null;
  sessionId: string | null;
  couponId: string | null;
  couponCode: string | null;
  couponType: CouponType | null;
  couponDiscountAmount: number;
  couponDiscountPercentage: number | null;
  couponMaxDiscount: number | null;
  couponAppliedAt: string | null;
  salarCode: string | null;
  salarCodeUserId: string | null;
  salarCodeCommissionType: string | null;
  salarCodeDiscountAmount: number;
  salarCodeAppliedAt: string | null;
  voucherId: string | null;
  voucherType: string | null;
  voucherAppliedAmount: number;
  voucherAvailableBalance: number | null;
  voucherAppliedAt: string | null;
  deliveryOptionId: string | null;
  deliveryOptionName: string | null;
  deliveryOptionPrice: number;
  deliveryEstimatedDays: string | null;
  deliveryIsFree: boolean;
  itemsTotal: number;
  itemsCount: number;
  totalDiscount: number;
  subtotal: number;
  deliveryCharges: number;
  platformFee: number;
  taxAmount: number;
  gstAmount: number;
  grandTotal: number;
  savedAmount: number;
  salarCoinsEarned: number;
  metadata: Record<string, any> | null;
  expiresAt: string | null;
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
  items: CartItem[];
}

export interface AddToCartRequest {
  productId: string;
  productName: string;
  productImage?: string;
  productType: ProductType;
  variantId?: string;
  variantName?: string;
  quantity: number;
  unitPrice: number;
  sellerId?: string;
  sellerName?: string;
  referralId?: string;
  isMLMProduct?: boolean;
  metadata?: Record<string, any>;
  isAdminProduct?: boolean;
}

export interface BulkAddToCartRequest {
  items: AddToCartRequest[];
  cartType?: CartType;
}

export interface UpdateCartItemRequest {
  quantity: number;
}

export interface ApplyReferralRequest {
  invitationCode: string;
}

export interface ApplyCouponRequest {
  code: string;
  type?: CouponType;
}

export interface ApplySalarCodeRequest {
  code: string;
  commissionType?: string;
}

export interface ApplyVoucherRequest {
  voucherId: string;
  amount?: number;
}

export interface SelectDeliveryOptionRequest {
  optionId: string;
  name: string;
  price: number;
  estimatedDays?: string;
  isFree?: boolean;
  courierCompanyId?: number;
}

export interface SelectAddressRequest {
  addressId: string;
}

export interface ValidateCartRequest {
  checkStock?: boolean;
  checkPrice?: boolean;
  checkDelivery?: boolean;
}

export interface AvailableCartCoupon {
  couponId: string;
  couponCode: string;
  couponTitle: string;
  discountType: string;
  discountValue: number;
  estimatedSavings: number;
  endDate?: string;
}

export interface CartState {
  cart: Cart | null;
  carts: Cart[];
  savedForLaterItems: CartItem[];
  summary: CartSummary | null;
  mlmItems: CartItem[];
  ecommerceItems: CartItem[];
  availableCoupons: AvailableCartCoupon[];
  availableCouponsLoading: boolean;
  loading: boolean;
  error: string | null;
  success: boolean;
  message: string | null;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}
